import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { InputFormField } from '@/components/formFields/InputFormField';
import {
  Manufacturer,
  useCreateManufacturer,
  useUpdateManufacturer,
} from '../../hooks/useManufacturer';
import { getFieldErrors } from '@/utils/apiError';
import { DialogClose } from '@radix-ui/react-dialog';

type ManufacturerModalProps = {
  visible: boolean;
  title: string;
  formProps?: Manufacturer | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
};

const ValidationSchema = Yup.object().shape({
  title: Yup.string().nullable().trim().required('Povinné pole').max(255, 'Maximálně 255 znaků'),
  url: Yup.string()
    .nullable()
    .trim()
    .required('Povinné pole')
    .url('Zadejte platnou URL (např. https://example.com)')
    .max(255, 'Maximálně 255 znaků'),
});

const ManufacturerModal = ({
  visible,
  title,
  formProps,
  onClose,
  onSaved,
}: ManufacturerModalProps) => {
  const create = useCreateManufacturer();
  const update = useUpdateManufacturer();

  const form = useFormik({
    initialValues: {
      title: formProps?.title || '',
      url: formProps?.url || '',
    },
    enableReinitialize: true,
    validationSchema: ValidationSchema,
    onSubmit: (values, { setErrors }) => {
      const payload = { title: values.title, url: values.url || null };
      const onSuccess = (res: { data: Manufacturer }) => {
        onSaved?.(res.data.id);
        onClose();
      };
      const onError = (error: unknown) => setErrors(getFieldErrors(error));

      if (formProps?.id) {
        update.mutate({ id: formProps.id, values: payload }, { onSuccess, onError });
      } else {
        create.mutate(payload, { onSuccess, onError });
      }
    },
  });

  const isSaving = create.isLoading || update.isLoading;

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <FormikProvider value={form}>
            <form onSubmit={form.handleSubmit} className="space-y-3">
              <InputFormField name="title" label="Název" placeholder="Název" required />
              <InputFormField name="url" label="Web" placeholder="https://..." required />
            </form>
          </FormikProvider>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid="manufacturer-modal-close-btn">
              Zavřít
            </Button>
          </DialogClose>

          <Button
            onClick={() => form.submitForm()}
            disabled={isSaving}
            data-testid="manufacturer-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManufacturerModal;
